import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for override operations
export const createOrUpdateOverride = createAsyncThunk(
  'overrides/createOrUpdate',
  async ({ loanId, fieldName, overrideType, overriddenValue, overriddenKey, comment, createdBy }) => {
    const response = await api.post('/overrides', {
      loan_id: loanId,
      field_name: fieldName,
      override_type: overrideType,
      overridden_value: overriddenValue,
      overridden_key: overriddenKey,
      comment,
      created_by: createdBy
    });
    return response.data;
  }
);

export const getOverridesForLoan = createAsyncThunk(
  'overrides/getForLoan',
  async ({ loanId, source = 'unknown' }) => {
    const response = await api.get(`/overrides/${loanId}?from=${source}`);
    return response.data;
  }
);

export const deleteOverride = createAsyncThunk(
  'overrides/delete',
  async (overrideId) => {
    await api.delete(`/overrides/${overrideId}`);
    return overrideId;
  }
);

const overridesSlice = createSlice({
  name: 'overrides',
  initialState: {
    overrides: [], // Array of override objects
    loading: {
      create: false,
      fetch: false,
      delete: false
    },
    errors: {
      create: null,
      fetch: null,
      delete: null
    }
  },
  reducers: {
    clearOverrides: (state) => {
      state.overrides = [];
      state.errors = { create: null, fetch: null, delete: null };
    },
    clearErrors: (state) => {
      state.errors = { create: null, fetch: null, delete: null };
    }
  },
  extraReducers: (builder) => {
    // Create/Update override
    builder
      .addCase(createOrUpdateOverride.pending, (state) => {
        state.loading.create = true;
        state.errors.create = null;
      })
      .addCase(createOrUpdateOverride.fulfilled, (state, action) => {
        state.loading.create = false;
        // Update or add the override to the array
        const existingIndex = state.overrides.findIndex(
          override => override.id === action.payload.id
        );
        if (existingIndex >= 0) {
          state.overrides[existingIndex] = action.payload;
        } else {
          state.overrides.push(action.payload);
        }
      })
      .addCase(createOrUpdateOverride.rejected, (state, action) => {
        state.loading.create = false;
        state.errors.create = action.error.message;
      });

    // Get overrides for loan
    builder
      .addCase(getOverridesForLoan.pending, (state) => {
        state.loading.fetch = true;
        state.errors.fetch = null;
      })
      .addCase(getOverridesForLoan.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.overrides = action.payload;
      })
      .addCase(getOverridesForLoan.rejected, (state, action) => {
        state.loading.fetch = false;
        state.errors.fetch = action.error.message;
      });

    // Delete override
    builder
      .addCase(deleteOverride.pending, (state) => {
        state.loading.delete = true;
        state.errors.delete = null;
      })
      .addCase(deleteOverride.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.overrides = state.overrides.filter(
          override => override.id !== action.payload
        );
      })
      .addCase(deleteOverride.rejected, (state, action) => {
        state.loading.delete = false;
        state.errors.delete = action.error.message;
      });
  }
});

export const { clearOverrides, clearErrors } = overridesSlice.actions;
export default overridesSlice.reducer;
